import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  Search,
  Textarea,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { useMedHistoryConditionSearch } from "@/features/Charting/network/useMedHistoryConditionSearch";
import { trackEvent } from "@/lib/tracking";
import { constants, getCodeValue, getDisplay } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import type { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import {
  medicalHistoryFormSchema,
  medicalHistoryNoteFormSchema,
} from "../schemas";
import type { MedicalHistory } from "../types";

const normalizeNote = (note: string | null | undefined) => note?.trim() ?? null;

const isNoteSaveDisabled = (
  note: string | null | undefined,
  initialNote: string,
) => {
  const normalizedNote = normalizeNote(note);

  return (
    ((note?.length ?? 0) > 0 && normalizedNote === "") ||
    normalizedNote === normalizeNote(initialNote)
  );
};

interface MedicalHistoryFormProps {
  patientId: string;
  medicalHistory?: MedicalHistory;
  onSubmit: (
    data: z.infer<typeof medicalHistoryFormSchema> &
      Partial<z.infer<typeof medicalHistoryNoteFormSchema>>,
  ) => void;
  onCancel?: () => void;
  isSubmitting: boolean;
  isSingleEncounterVisitsEnabled?: boolean;
  /** When true, conditions with an existing episode of care are shown (labeled "From Medical History") instead of filtered out. */
  includeExistingEpisodes?: boolean;
}

export const MedicalHistoryForm = ({
  patientId,
  medicalHistory,
  onSubmit,
  onCancel,
  isSubmitting,
  isSingleEncounterVisitsEnabled = false,
  includeExistingEpisodes = false,
}: MedicalHistoryFormProps) => {
  const isNoteMode = isSingleEncounterVisitsEnabled && Boolean(medicalHistory);
  const initialNote = medicalHistory?.condition.note?.[0]?.text ?? "";
  const [searchTerm, setSearchTerm] = useState("");
  const { data: conditions, isFetching: isFetchingConditions } =
    useMedHistoryConditionSearch({
      searchTerm,
      patientId,
      enabled: !isNoteMode,
    });
  const form = useForm<
    z.infer<typeof medicalHistoryFormSchema> &
      Partial<z.infer<typeof medicalHistoryNoteFormSchema>>
  >({
    resolver: zodResolver(
      isNoteMode ? medicalHistoryNoteFormSchema : medicalHistoryFormSchema,
    ),
    defaultValues: {
      conditionCode: medicalHistory?.condition?.code
        ? getCodeValue(
            medicalHistory.condition.code,
            constants.SNOMED_SYSTEM_URL,
          )
        : undefined,
      conditionCodeDisplay: medicalHistory?.condition?.code
        ? getDisplay(medicalHistory.condition.code)
        : undefined,
      note: initialNote,
    },
  });

  const watchedNote = useWatch({ control: form.control, name: "note" });
  const watchedConditionCode = useWatch({
    control: form.control,
    name: "conditionCode",
  });
  const initialConditionCode = medicalHistory?.condition.code?.coding?.[0].code;
  const [disabledSaving, setDisabledSaving] = useState(true);
  useEffect(() => {
    setDisabledSaving(
      isNoteMode
        ? isNoteSaveDisabled(watchedNote, initialNote)
        : watchedConditionCode === initialConditionCode,
    );
  }, [
    initialNote,
    isNoteMode,
    initialConditionCode,
    watchedConditionCode,
    watchedNote,
  ]);
  const submit = (
    data: z.infer<typeof medicalHistoryFormSchema> &
      Partial<z.infer<typeof medicalHistoryNoteFormSchema>>,
  ) => {
    if (medicalHistory) {
      trackEvent(TRACKERS.EDIT_CONDITION);
    }
    onSubmit(isNoteMode ? { ...data, note: normalizeNote(data.note) } : data);
    setDisabledSaving(true);
  };
  const handleNoteCancel = () => {
    form.setValue("note", initialNote);
    form.clearErrors("note");
    setDisabledSaving(true);
  };
  return (
    <div>
      <form
        onSubmit={form.handleSubmit(submit)}
        className="flex flex-col gap-2"
      >
        {medicalHistory ? null : (
          <BodyText size="sm" bold>
            Add Medical History
          </BodyText>
        )}
        {isNoteMode ? (
          <Field>
            <FieldLabel htmlFor="note">Note</FieldLabel>
            <Textarea
              id="note"
              placeholder="Add a note"
              {...form.register("note")}
            />
            <FieldError errors={[form.formState.errors.note]} />
          </Field>
        ) : (
          <div className="flex flex-row gap-2">
            <Controller
              control={form.control}
              name="conditionCode"
              render={({ fieldState }) => (
                <Field className="flex-1">
                  <FieldLabel htmlFor="conditionCode">Condition</FieldLabel>
                  <Search
                    input={form.getValues("conditionCodeDisplay")}
                    leftIcon={SearchIcon}
                    placeholder="Search for a condition"
                    options={
                      (includeExistingEpisodes
                        ? conditions
                            ?.map((code) => ({
                              label: code.conditionDisplay,
                              value: code.conditionCode,
                              subLabel: code.existingEpisodeOfCareId
                                ? "(From Medical History)"
                                : undefined,
                              metadata: {
                                system: code.conditionSystem,
                              },
                            }))
                            .sort((a, _) => (a.subLabel ? -1 : 1))
                        : conditions
                            ?.filter((code) => !code.existingEpisodeOfCareId)
                            .map((code) => ({
                              label: code.conditionDisplay,
                              value: code.conditionCode,
                              metadata: {
                                system: code.conditionSystem,
                              },
                            }))) || []
                    }
                    isLoading={isFetchingConditions}
                    onInputChange={(input) => {
                      setSearchTerm(input);
                    }}
                    onItemSelected={(item) => {
                      form.setValue("conditionCode", item.value as string);
                      form.setValue("conditionCodeDisplay", item.label);
                      if (includeExistingEpisodes) {
                        const selectedCondition = conditions?.find(
                          (c) => c.conditionCode === item.value,
                        );
                        form.setValue(
                          "existingEpisodeOfCareId",
                          selectedCondition?.existingEpisodeOfCareId,
                        );
                      }
                    }}
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
          </div>
        )}
        <SubmitCancelButtons
          isSaving={isSubmitting}
          onCancel={
            isNoteMode
              ? disabledSaving
                ? undefined
                : handleNoteCancel
              : onCancel
          }
          savingDisabled={disabledSaving}
        />
      </form>
    </div>
  );
};
